import { batch, Component } from 'solid-js';
import type { RgbColor } from 'solid-color';
import { ColorSwatch } from '../../components/ColorSwatch';
import { STOP_ROW_HEIGHT_PX } from '../../heatmap-stop-layout';
import { PaletteStopColorPicker } from './PaletteStopColorPicker';
import { createPaletteStopColorPickerControls } from './createPaletteStopColorPickerControls';

interface OverflowStopRowProps {
  readonly color: string;
  readonly label: string;
  readonly testId: string;
  readonly editable: boolean;
  /** Called when the user saves a new colour from the swatch colour picker. */
  onColorChange?(color: string): void;
}

/**
 * Out-of-range (above / below) palette row: colour swatch and label. Pass
 * `editable={false}` for read-only display; `editable={true}` plus
 * `onColorChange` makes the swatch clickable and opens a colour picker so the
 * out-of-range cap colour can be edited.
 */
export const OverflowStopRow: Component<OverflowStopRowProps> = (props) => {
  const colorPicker = createPaletteStopColorPickerControls();

  const canPickColor = (): boolean =>
    props.editable && props.onColorChange !== undefined;

  function handleSwatchClick(): void {
    if (!canPickColor()) return;
    colorPicker.toggleColorPicker();
  }

  function handleColorChange(rgb: RgbColor): void {
    props.onColorChange?.(colorPicker.applyColor(rgb));
  }

  function handleColorSave(rgb: RgbColor): void {
    batch(() => {
      props.onColorChange?.(colorPicker.saveColor(rgb));
    });
  }

  return (
    <div
      class="flex w-full items-center gap-1.5"
      style={{ height: `${String(STOP_ROW_HEIGHT_PX)}px` }}
      data-testid={props.testId}
    >
      <div class="relative shrink-0" ref={colorPicker.setSwatchAnchor}>
        <ColorSwatch
          color={props.color}
          testId={`${props.testId}-swatch`}
          selected={canPickColor() && colorPicker.showColorPicker()}
          onClick={canPickColor() ? handleSwatchClick : undefined}
        />
        <PaletteStopColorPicker
          useTransparency={true}
          show={colorPicker.showColorPicker() && canPickColor()}
          anchorEl={colorPicker.getSwatchAnchor()}
          color={props.color}
          testId={`${props.testId}-color-picker`}
          onChange={handleColorChange}
          onSave={handleColorSave}
          onClose={colorPicker.closeColorPicker}
        />
      </div>
      <div class="flex h-7 min-w-0 flex-1 items-center">
        <span class="text-sm text-gray-6">{props.label}</span>
      </div>
    </div>
  );
};
